import axios from "axios";
import { getToken } from "./utilsApi";
import { parseCsv } from "./demographicsService";

/**
 * Student class schedules import (Nov 2026). DiscloSure admins only (enforced by the server).
 * Schedules are uploaded but never read back here — the server only returns counts. Responders
 * see "where the student should be now" on emergency reports only.
 */
const url = () => `${process.env.REACT_APP_apiBaseUrl}schedules`;
export const ROWS_PER_REQUEST = 500;

const fail = (error) => ({
  ok: false,
  message: error?.response?.data?.message || "Something went wrong. Please try again.",
});

export const schedulesSummary = async (district) => {
  const token = await getToken();
  try {
    const res = await axios({ method: "GET", url: url(), headers: { "x-api-key": token }, params: { district } });
    return { ok: true, ...res.data };
  } catch (error) {
    return fail(error);
  }
};

export const uploadSchedules = async (district, rows) => {
  const token = await getToken();
  try {
    const res = await axios({
      method: "POST",
      url: url(),
      headers: { "x-api-key": token, "Content-Type": "application/json" },
      data: { district, rows },
      timeout: 30000,
    });
    return { ok: true, ...res.data };
  } catch (error) {
    return fail(error);
  }
};

export const TEMPLATE_CSV =
  "Email,Student Number,Period,Course,Room,Teacher,Start Time,End Time,Days\n" +
  "student@district.org,100234,1,Algebra I,214,Mr. Lee,8:00 AM,8:50 AM,\n" +
  "student@district.org,100234,2,English 9,201,Ms. Park,8:55 AM,9:45 AM,\n" +
  "student@district.org,100234,3,PE,Gym,Coach Ray,9:50 AM,10:40 AM,MWF\n" +
  "student@district.org,100234,3,Spanish I,105,Sr. Diaz,9:50 AM,10:40 AM,TTh\n";

const HEADER_ALIASES = {
  email: ["email", "student email", "email address", "studentemail"],
  studentNumber: ["student number", "studentnumber", "student id", "studentid", "sis id", "local id", "id"],
  period: ["period", "per", "block", "period/block"],
  course: ["course", "class", "course name", "class name", "subject", "section"],
  room: ["room", "room number", "classroom", "location"],
  teacher: ["teacher", "instructor", "teacher name"],
  start: ["start time", "start", "begins", "time in"],
  end: ["end time", "end", "ends", "time out"],
  days: ["days", "day", "meeting days", "rotation"],
};

/** CSV text -> { rows: [{..., _row}], missing: [...] }. _row = the sheet's row number. */
export const csvToScheduleRows = (text) => {
  const table = parseCsv(text);
  if (!table.length) return { rows: [], missing: ["a header row"] };
  const header = table[0].map((h) => h.trim().toLowerCase());
  const col = {};
  for (const [field, names] of Object.entries(HEADER_ALIASES)) {
    const idx = header.findIndex((h) => names.includes(h));
    if (idx >= 0) col[field] = idx;
  }
  const missing = [];
  if (col.email === undefined && col.studentNumber === undefined) missing.push("an Email or Student Number column");
  if (col.course === undefined && col.period === undefined) missing.push("a Course or Period column");
  const rows = table.slice(1).map((r, i) => {
    const out = { _row: i + 2 };
    for (const [field, idx] of Object.entries(col)) out[field] = (r[idx] || "").trim();
    return out;
  });
  return { rows, missing };
};

/** Split rows into requests of at most `size`, never splitting one student's classes (the server
 *  replaces a student's whole schedule with the rows it gets in one request). */
export const chunkByStudent = (rows, size = ROWS_PER_REQUEST) => {
  const groups = new Map();
  for (const r of rows) {
    const email = (r.email || "").toLowerCase();
    const number = (r.studentNumber || "").toLowerCase();
    const key = email ? `e:${email}` : number ? `n:${number}` : `row:${r._row}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  }
  const chunks = [];
  let current = [];
  for (const group of groups.values()) {
    if (current.length && current.length + group.length > size) { chunks.push(current); current = []; }
    current.push(...group); // a single student never has anywhere near `size` classes
  }
  if (current.length) chunks.push(current);
  return chunks;
};
